import React, { useState, useEffect, useRef, KeyboardEvent } from 'react';
import './AddChampion.css';
import DataService from '../../../DataService';

interface Champion {
  id: string;
  name: string;
  imageUrl: string;
}

export interface ChampionAddData {
  name: string;
  level: number;
}

interface AddChampionProps {
  onSelectChampion: (championData: ChampionAddData) => void;
}

const AddChampion: React.FC<AddChampionProps> = ({ onSelectChampion }) => {
  const [championsList, setChampionsList] = useState<Champion[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState<Champion[]>([]);
  const [selectedChampion, setSelectedChampion] = useState<Champion | null>(null);
  const [previouslySelected, setPreviouslySelected] = useState<string | null>(null);
  const [level, setLevel] = useState(16);
  const [isLoading] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null);

  /** Normalize for fuzzy search: strip apostrophes, periods, spaces, hyphens and lowercase */
  const normalizeForSearch = (str: string): string => {
    return str.toLowerCase().replace(/['\.\s-]/g, '');
  };

  // Fetch champions on component mount
  useEffect(() => {
    const fetchChampions = async () => {
      try {
        // Fetch latest version dynamically
        const version = await DataService.fetchLatestVersion();
        
        // Fetch champions
        const response = await fetch(
          `https://ddragon.leagueoflegends.com/cdn/${version}/data/en_US/champion.json`
        );
        const data = await response.json();

        // Create our standard champion array
        const championsData = Object.values(data.data).map((champion: any) => ({
          id: champion.id,
          name: champion.name,
          imageUrl: `https://ddragon.leagueoflegends.com/cdn/${version}/img/champion/${champion.id}.png`,
        }));

        // Append the "Practice Dummy" champion to the list
        championsData.push({
          id: 'DummyChampion',
          name: 'DummyChampion',
          imageUrl:
            'https://raw.communitydragon.org/latest/plugins/rcp-be-lol-game-data/global/default/assets/characters/tft_trainingdummy/hud/icons2d/tft_trainingdummy_donothing.png',
        });

        setChampionsList(championsData);
      } catch (error) {
        console.error('Error fetching champions:', error);
      }
    };

    fetchChampions();
    
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    setSearchInput(input);

    if (input.length > 0) {
      const normalizedInput = normalizeForSearch(input);
      // Filter champions with normalized matching, then sort with starts-with priority
      const filteredChampions = championsList
        .filter((champion) => {
          const normalizedName = normalizeForSearch(champion.name);
          const normalizedId = normalizeForSearch(champion.id);
          return normalizedName.includes(normalizedInput) || normalizedId.includes(normalizedInput);
        })
        .sort((a, b) => {
          const normA = normalizeForSearch(a.name);
          const normB = normalizeForSearch(b.name);
          const aStartsWith = normA.startsWith(normalizedInput);
          const bStartsWith = normB.startsWith(normalizedInput);
          // 1. Prioritize names that start with the query
          if (aStartsWith && !bStartsWith) return -1;
          if (!aStartsWith && bStartsWith) return 1;
          // 2. Alphabetical within same group
          return a.name.localeCompare(b.name);
        })
        .slice(0, 8);
      setSuggestions(filteredChampions);
      
      // Auto-select first suggestion if available
      if (filteredChampions.length > 0) {
        setSelectedChampion(filteredChampions[0]);
        setPreviouslySelected(null); // Reset previous selection
      } else {
        setSelectedChampion(null);
        setPreviouslySelected(null);
      }
    } else {
      setSuggestions([]);
      setSelectedChampion(null);
      setPreviouslySelected(null);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && selectedChampion) {
      handleConfirmSelection();
    }
  };

  const handleConfirmSelection = () => {
    if (selectedChampion) {
      const championData: ChampionAddData = {
        name: selectedChampion.name,
        level: level
      };
      
      onSelectChampion(championData);
    }
  };

  const handleLevelInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 1 && value <= 18) {
      setLevel(value);
    } else if (e.target.value === '') {
      // Allow empty input temporarily
      setLevel(1);
    }
  };

  const handleChampionSelect = (champion: Champion) => {
    // If this champion is already selected, confirm selection
    if (selectedChampion && selectedChampion.id === champion.id && previouslySelected === champion.id) {
      handleConfirmSelection();
      return;
    }
    
    // Otherwise, just select the champion
    setSelectedChampion(champion);
    setPreviouslySelected(champion.id);
  };

  return (
    <div className='add-champion'>
    
        <div className='champion-search-container'>
          <h3 className="search-title">Add Champion</h3>
          
          <div className='search-bar'>
            <input
              ref={inputRef}
              type='text'
              placeholder='Search for a champion'
              value={searchInput}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              className="champion-search-input"
            />
            
            {suggestions.length > 0 && (
              <ul className='champion-suggestions-list'>
                {suggestions.map((champion) => (
                  <li
                    key={champion.id}
                    className={`champion-suggestion-item ${selectedChampion?.id === champion.id ? 'selected' : ''}`}
                    onClick={() => handleChampionSelect(champion)}
                  >
                    <img src={champion.imageUrl} alt={champion.name} />
                    <span>{champion.name}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          
          {selectedChampion && (
            <div className="champion-selection-panel">
              <div className="selected-champion-info">
                <img 
                  src={selectedChampion.imageUrl} 
                  alt={selectedChampion.name} 
                  className="selected-champion-image" 
                  onClick={() => handleConfirmSelection()}
                />
                <div className="champion-details">
                  <span 
                    className="selected-champion-name"
                    onClick={() => handleConfirmSelection()}
                  >
                    {selectedChampion.name}
                  </span>
                </div>
                
                <div className="level-selector">
                  <label htmlFor="level-input">Level:</label>
                  <input
                    id="level-input"
                    type="number"
                    min="1"
                    max="18"
                    value={level}
                    onChange={handleLevelInputChange}
                    className="level-input"
                  />
                </div>
              </div>
              
              <div className="champion-selection-actions">
                <button 
                  className="champion-action-button confirm-button"
                  onClick={handleConfirmSelection}
                  disabled={isLoading}
                >
                  Add Champion
                </button>
              </div>
            </div>
          )}
        </div>
    
    </div>
  );
};

export default AddChampion;